// src/components/BeforeAfterSlider.jsx
import React, { useRef, useState, useEffect } from 'react';

const BeforeAfterSlider = ({ beforeSrc, afterSrc, title, description }) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const updatePosition = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(rect.width, x)); // clamp inside bounds
    const newPos = (x / rect.width) * 100;
    setPosition(newPos);
  };

  // Throttled move handlers to improve performance on touch/mouse drag
  useEffect(() => {
    let rafId = null;

    const onMove = (e) => {
      if (!isDragging) return;
      e.preventDefault(); // prevent scroll on touchmove
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => updatePosition(clientX));
    };

    const onEnd = () => {
      setIsDragging(false);
      if (rafId) cancelAnimationFrame(rafId);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isDragging]);

  return (
    <div className="flex flex-col h-full w-full">
      {/* Optional header */}
      {(title || description) && (
        <div className="mb-3 px-1">
          {title && (
            <h4 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-white">
              {title}
            </h4>
          )}
          {description && (
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
              {description}
            </p>
          )}
        </div>
      )}

      {/* Main slider */}
      <div
        ref={containerRef}
        className={`
          relative w-full aspect-[4/3] sm:aspect-[5/4] overflow-hidden 
          rounded-xl shadow-inner bg-gray-800 cursor-col-resize select-none
          touch-none
        `}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
          updatePosition(e.clientX);
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          setIsDragging(true);
          updatePosition(e.touches[0].clientX);
        }}
      >
        {/* After – always visible underneath */}
        <img
          src={afterSrc}
          alt="After treatment result"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />

        {/* Before – clipped */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${position}%` }}
        >
          <img
            src={beforeSrc}
            alt="Before treatment"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        </div>

        {/* Handle + line */}
        <div
          className="absolute inset-y-0 w-1.5 bg-white/90 shadow-2xl pointer-events-none"
          style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
        >
          <div className="
            absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            w-11 h-11 sm:w-12 sm:h-12 bg-white rounded-full 
            shadow-xl border-4 border-gray-200 flex items-center justify-center
            transition-transform duration-150 active:scale-95
          ">
            <svg
              className="w-6 h-6 sm:w-7 sm:h-7 text-gray-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 9l4-4 4 4m0 6l-4 4-4-4"
              />
            </svg>
          </div>
        </div>

        {/* Labels – more visible on mobile */}
        <div className="absolute top-3 left-3 bg-black/70 text-white text-xs font-bold px-2.5 py-1 rounded-full z-20 backdrop-blur-sm pointer-events-none">
          BEFORE
        </div>
        <div className="absolute top-3 right-3 bg-black/70 text-white text-xs font-bold px-2.5 py-1 rounded-full z-20 backdrop-blur-sm pointer-events-none">
          AFTER
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;